/**
 * Remove a skill prompt from the user's active Prompt Lab session.
 *
 * Identified by `skillPromptId` (the per-session id, not the library
 * row id). Read the session via readPromptSession first to get the id.
 *
 * Approval-gated — yanking content out of the user's prompt is the
 * same severity as setSystemPrompt; the user must accept.
 */

import type { NipeTool } from './types';

export interface RemoveSkillPromptContext {
  getActivePromptSessionId?: () => string | null;
  removeSkillPromptFromSession?: (
    sessionId: string,
    skillPromptId: string,
  ) => { ok: true } | { ok: false; reason: string };
}

export const removeSkillPromptTool: NipeTool<RemoveSkillPromptContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'removeSkillPrompt',
      description:
        "Remove a skill prompt from the user's active Prompt Lab session. " +
        'Identify by `skillPromptId` — the per-session id you got from ' +
        'readPromptSession. Always read first so you delete the right slot.',
      parameters: {
        type: 'object',
        properties: {
          skillPromptId: {
            type: 'string',
            description:
              'The session-scoped id of the skill prompt to remove ' +
              '(from readPromptSession().session.skillPrompts[].id).',
          },
        },
        required: ['skillPromptId'],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: true,
  handler: async (args, ctx) => {
    if (!ctx.getActivePromptSessionId || !ctx.removeSkillPromptFromSession) {
      return {
        ok: false,
        reason: 'Prompt Lab is not mounted in this session.',
      };
    }
    const sessionId = ctx.getActivePromptSessionId();
    if (sessionId === null) {
      return {
        ok: false,
        reason: 'No active prompt session yet.',
      };
    }
    const skillPromptId =
      typeof args.skillPromptId === 'string' ? args.skillPromptId.trim() : '';
    if (skillPromptId.length === 0) {
      return { ok: false, reason: '`skillPromptId` is required.' };
    }
    return ctx.removeSkillPromptFromSession(sessionId, skillPromptId);
  },
};
